import { getBackendUrl } from '../lib/api';
import { useEffect, useState } from 'react';

/**
 * SeoHead — dynamically injects per-page SEO meta tags.
 *
 * Props:
 *   pageTitle       string   Override global <title> for this page.
 *   pageDescription string   Override global meta description for this page.
 *   canonical       string   If set, injects <link rel="canonical" href="...">.
 *   noindex         boolean  If true, injects <meta name="robots" content="noindex, nofollow">
 *                            to keep transient / token-based pages out of Google.
 *   jsonLd          object   Additional schema.org JSON-LD to inject on this page
 *                            (in ADDITION to the global Organization/EmploymentAgency
 *                            already declared in index.html).
 *   ogImage         string   Per-page Open Graph image URL (absolute or relative).
 *                            Overrides the global og_image_url from /api/seo-settings.
 *                            Used by Facebook, LinkedIn, Twitter, WhatsApp, Slack, etc.
 *   ogImageWidth    number   Width of ogImage (default 1200).
 *   ogImageHeight   number   Height of ogImage (default 630).
 */
const SeoHead = ({
  pageTitle = null,
  pageDescription = null,
  canonical = null,
  noindex = false,
  jsonLd = null,
  ogImage = null,
  ogImageWidth = null,
  ogImageHeight = null,
}) => {
  const [seoData, setSeoData] = useState(null);

  const getAbsoluteUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const backendUrl = getBackendUrl() || window.location.origin;
    return `${backendUrl}${url.startsWith('/') ? '' : '/'}${url}`;
  };

  useEffect(() => {
    const fetchSeoSettings = async () => {
      try {
        const response = await fetch(getBackendUrl() + '/api/seo-settings');
        if (response.ok) {
          const data = await response.json();
          setSeoData(data);
        }
      } catch (error) {
        console.error('Failed to fetch SEO settings:', error);
      }
    };
    fetchSeoSettings();
  }, []);

  useEffect(() => {
    // Track dynamically-created nodes so we can remove them on unmount
    // — avoids leaking per-page tags onto the next page.
    const createdNodes = [];

    const title = pageTitle || seoData?.meta_title;
    const description = pageDescription || seoData?.meta_description;
    // Per-page ogImage takes priority over the global one stored in seoData
    const ogImageUrl = ogImage
      ? getAbsoluteUrl(ogImage)
      : getAbsoluteUrl(seoData?.og_image_url);
    const ogW = ogImage && ogImageWidth ? String(ogImageWidth) : '1200';
    const ogH = ogImage && ogImageHeight ? String(ogImageHeight) : '630';
    const faviconUrl = getAbsoluteUrl(seoData?.favicon_url);

    if (title) document.title = title;

    const upsertMeta = (selector, key, value, content) => {
      if (!content) return;
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(key, value);
        document.head.appendChild(el);
        createdNodes.push(el);
      }
      el.setAttribute('content', content);
    };

    upsertMeta('meta[name="description"]', 'name', 'description', description);
    upsertMeta('meta[property="og:title"]', 'property', 'og:title', title);
    upsertMeta('meta[property="og:description"]', 'property', 'og:description', description);
    upsertMeta('meta[property="og:type"]', 'property', 'og:type', 'website');
    upsertMeta('meta[property="og:url"]', 'property', 'og:url', window.location.href);
    if (ogImageUrl) {
      upsertMeta('meta[property="og:image"]', 'property', 'og:image', ogImageUrl);
      upsertMeta('meta[property="og:image:width"]', 'property', 'og:image:width', ogW);
      upsertMeta('meta[property="og:image:height"]', 'property', 'og:image:height', ogH);
      upsertMeta('meta[property="og:image:alt"]', 'property', 'og:image:alt', title || 'Talent Partner');
    }
    upsertMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
    upsertMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title);
    upsertMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description);
    if (ogImageUrl) upsertMeta('meta[name="twitter:image"]', 'name', 'twitter:image', ogImageUrl);

    // Robots directive — set to noindex for transient / token-only pages
    let robotsTag = document.querySelector('meta[name="robots"]');
    const robotsContent = noindex ? 'noindex, nofollow' : 'index, follow';
    if (!robotsTag) {
      robotsTag = document.createElement('meta');
      robotsTag.setAttribute('name', 'robots');
      document.head.appendChild(robotsTag);
      createdNodes.push(robotsTag);
    }
    robotsTag.setAttribute('content', robotsContent);

    // Canonical URL
    if (canonical) {
      let canonicalTag = document.querySelector('link[rel="canonical"]');
      if (!canonicalTag) {
        canonicalTag = document.createElement('link');
        canonicalTag.rel = 'canonical';
        document.head.appendChild(canonicalTag);
        createdNodes.push(canonicalTag);
      }
      canonicalTag.href = canonical;
    }

    // Additional JSON-LD (per-page) — injected in ADDITION to index.html global
    let jsonLdNode = null;
    if (jsonLd) {
      jsonLdNode = document.createElement('script');
      jsonLdNode.type = 'application/ld+json';
      jsonLdNode.setAttribute('data-seohead', 'page');
      jsonLdNode.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(jsonLdNode);
      createdNodes.push(jsonLdNode);
    }

    // Custom favicon from backend config (overrides default)
    if (faviconUrl) {
      const favicon = document.querySelector('link[rel="icon"]');
      if (favicon) favicon.href = faviconUrl;
    }

    return () => {
      // Remove only the nodes we created, preserve index.html defaults
      createdNodes.forEach((n) => {
        if (n && n.parentNode) n.parentNode.removeChild(n);
      });
    };
  }, [seoData, pageTitle, pageDescription, canonical, noindex, jsonLd, ogImage, ogImageWidth, ogImageHeight]);

  return null;
};

export default SeoHead;
